import { useState, useEffect, useCallback, lazy, Suspense, memo } from 'react';
import { BACKEND_URL, localISO } from './utils';
import { apiFetch } from './apiFetch';
import { ErrorBoundary } from './components/ErrorBoundary';
import { OfflineBanner } from './components/OfflineBanner';
import { BookingCalendar } from './components/BookingCalendar';
import { useTrackingPixels } from './hooks/useTrackingPixels';
import { LanguageProvider } from './i18n';
import { FeedbackProvider } from './components/feedback/FeedbackProvider';
import './styles.css';

// Lazy load non-critical routes for code splitting
const StaffLogin = lazy(() => import('./components/StaffDashboard').then(m => ({ default: m.StaffLogin })));
const StaffDashboard = lazy(() => import('./components/StaffDashboard').then(m => ({ default: m.StaffDashboard })));
const AdminLogin = lazy(() => import('./components/admin/auth/AdminLogin').then(m => ({ default: m.AdminLogin })));
const AdminPanel = lazy(() => import('./components/AdminPanel').then(m => ({ default: m.AdminPanel })));
const ReschedulePage = lazy(() => import('./components/ReschedulePage').then(m => ({ default: m.ReschedulePage })));
const TourManagePage = lazy(() => import('./components/TourManagePage').then(m => ({ default: m.TourManagePage })));
const TourBookingPage = lazy(() => import('./components/TourBookingPage').then(m => ({ default: m.TourBookingPage })));
const BookingConfirmationPage = lazy(() => import('./components/BookingConfirmationPage').then(m => ({ default: m.BookingConfirmationPage })));
const StaffScanPage = lazy(() => import('./components/BookingConfirmationPage').then(m => ({ default: m.StaffScanPage })));
const PrivacyPage = lazy(() => import('./components/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const NotFoundPage = lazy(() => import('./components/NotFoundPage').then(m => ({ default: m.NotFoundPage })));

async function loadConfig(adminToken = '') {
  try {
    const opts = adminToken ? { headers: { 'x-admin-token': adminToken } } : {};
    const res = await apiFetch('/api/config', opts);
    if (res.ok) {
      const c = await res.json();
      if (c) return c;
    }
  } catch(e) { console.error('loadConfig error', e); }
  return null;
}

async function saveConfig(c) {
  let token = '';
  try { token = JSON.parse(sessionStorage.getItem('admin_session') || '{}').token || ''; } catch {}
  // slots and tourSlots are backend-managed — strip them so saves stay lightweight
  // (the backend also strips them on PUT /api/config)
  // eslint-disable-next-line no-unused-vars
  const { slots: _slots, tourSlots: _tourSlots, ...payload } = c;
  const res = await fetch(`${BACKEND_URL}/api/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try { const j = await res.json(); detail = j.detail || detail; } catch {}
    throw new Error(`Save failed: ${detail}`);
  }
}

async function loadBookings() {
  try {
    const res = await apiFetch('/api/bookings');
    if (res.ok) {
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    }
  } catch(e) {}
  return [];
}

const KNOWN_ROUTES = [
  /^\/$/, /^\/book$/, /^\/tours$/, /^\/privacy$/, /^\/reschedule\/[^/]+\/[^/]+$/,
  /^\/booking\/[^/]+\/[^/]+$/, /^\/staff\/scan\/[^/]+$/, /^\/unsubscribe\/[^/]+$/,
  /^\/tour\/[^/]+\/[^/]+$/,
];

function isKnownRoute(path) {
  return KNOWN_ROUTES.some(r => r.test(path));
}

const LazyFallback = memo(() => (
  <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)'}}>
    <div className="spinner" style={{ width: 24, height: 24 }} />
  </div>
));

function AppContent() {
  const [view, setView] = useState('booking');
  const [config, setConfig] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const isShareablePage = window.location.pathname === '/book';
  const isToursPage = window.location.pathname === '/tours';
  const isPrivacyPage = window.location.pathname === '/privacy';
  const rescheduleMatch = window.location.pathname.match(/^\/reschedule\/([^/]+)\/([^/]+)$/);
  const isReschedulePage = !!rescheduleMatch;
  const confirmationMatch = window.location.pathname.match(/^\/booking\/([^/]+)\/([^/]+)$/);
  const isConfirmationPage = !!confirmationMatch;
  const tourManageMatch = window.location.pathname.match(/^\/tour\/([^/]+)\/([^/]+)$/);
  const isTourManagePage = !!tourManageMatch;
  const staffScanMatch = window.location.pathname.match(/^\/staff\/scan\/([^/]+)$/);
  const isStaffScanPage = !!staffScanMatch;

  const { trackEvent } = useTrackingPixels(config);

  useEffect(() => {
    (async () => {
      try {
        const c = await loadConfig();
        if (!c) { setError(true); setLoading(false); return; }
        setConfig(c);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Re-fetch config when the day changes (tab left open overnight)
  useEffect(() => {
    const id = setInterval(async () => {
      if (!config?.todayISO) return;
      if (localISO() > config.todayISO) {
        const fresh = await loadConfig();
        if (fresh) setConfig(fresh);
      }
    }, 60_000);
    return () => clearInterval(id);
  }, [config?.todayISO]);

  // Re-fetch config when the tab regains focus after >5 min of inactivity
  useEffect(() => {
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt > 5 * 60 * 1000) {
        loadConfig().then(c => { if (c) setConfig(c); });
        hiddenAt = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const handleSaveConfig = useCallback(async (c) => {
    await saveConfig(c);
    setConfig(c);
  }, []);

  const handleAddBooking = useCallback(async (b) => {
    if (b) {
      const res = await fetch(`${BACKEND_URL}/api/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(b),
      });
      if (!res.ok) {
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { throw new Error('Booking failed'); }
        throw new Error(data.detail || 'Booking failed');
      }
    }
  }, []);

  const pathname = window.location.pathname;

  // Check for 404 on unknown routes
  if (!isShareablePage && !isToursPage && !isPrivacyPage && !isReschedulePage && !isConfirmationPage && !isStaffScanPage && !isTourManagePage && pathname !== '/' && !isKnownRoute(pathname)) {
    return <Suspense fallback={<LazyFallback />}><NotFoundPage /></Suspense>;
  }

  if (isTourManagePage) {
    return <Suspense fallback={<LazyFallback />}><TourManagePage bookingRef={tourManageMatch[1]} token={tourManageMatch[2]} /></Suspense>;
  }

  if (isReschedulePage) {
    return <Suspense fallback={<LazyFallback />}><ReschedulePage bookingRef={rescheduleMatch[1]} rescheduleToken={rescheduleMatch[2]} /></Suspense>;
  }

  if (isConfirmationPage) {
    return <Suspense fallback={<LazyFallback />}><BookingConfirmationPage bookingRef={confirmationMatch[1]} token={confirmationMatch[2]} trackEvent={trackEvent} /></Suspense>;
  }

  if (isStaffScanPage) {
    return <Suspense fallback={<LazyFallback />}><StaffScanPage bookingRef={staffScanMatch[1]} /></Suspense>;
  }

  if (isPrivacyPage) {
    return <Suspense fallback={<LazyFallback />}><PrivacyPage /></Suspense>;
  }

  if (loading) {
    return (
      <div data-testid="loading-state" style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)'}}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ width: 24, height: 24, marginBottom: 12 }} />
          <p style={{color:'var(--bark)',fontFamily:'Jost, sans-serif',fontSize:14}}>Loading{'\u2026'}</p>
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div data-testid="error-state" style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)'}}>
        <p style={{color:'var(--bark)',fontFamily:'Jost, sans-serif',fontSize:16}}>Unable to connect. Please refresh the page.</p>
      </div>
    );
  }

  // Emergency Mode
  if (config.emergencyMode && (view === 'booking' || isShareablePage || isToursPage)) {
    return (
      <div data-testid="emergency-mode" style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--cream)',fontFamily:'Jost, sans-serif',textAlign:'center',padding:32}}>
        <div style={{maxWidth:460}}>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:400,color:'var(--bark)',marginBottom:12}}>Las Ruinas Bathhouse</h1>
          <p style={{fontSize:15,color:'var(--bark)',marginBottom:24,lineHeight:1.6}}>Online booking is temporarily unavailable. We apologize for the inconvenience.</p>
          <p style={{fontSize:14,color:'#8B7E74',marginBottom:8}}>Please contact us directly:</p>
          <p style={{fontSize:14,color:'var(--bark)'}}>Frontdesk@lasruinas.com &middot; +1 786-535-8054</p>
          {!isShareablePage && <button style={{marginTop:24,padding:'10px 24px',background:'var(--bark)',color:'#fff',border:'none',borderRadius:6,cursor:'pointer',fontSize:12,letterSpacing:1,textTransform:'uppercase',fontFamily:'Jost,sans-serif'}} onClick={() => setView('admin-login')}>Admin Access</button>}
        </div>
      </div>
    );
  }

  return (
    <>
      {(view === 'booking' || isShareablePage) && !isToursPage && (
        <BookingCalendar
          config={config}
          bookings={bookings}
          onBook={handleAddBooking}
          onGoAdmin={isShareablePage ? null : () => setView('admin-login')}
          onGoStaff={isShareablePage ? null : () => setView('staff-login')}
          onConfigUpdate={handleSaveConfig}
          isShareable={isShareablePage}
          trackEvent={trackEvent}
        />
      )}
      {view === 'booking' && isToursPage && (
        <Suspense fallback={<LazyFallback />}>
          <TourBookingPage
            config={config}
            onGoAdmin={() => setView('admin-login')}
            onGoStaff={() => setView('staff-login')}
            trackEvent={trackEvent}
          />
        </Suspense>
      )}
      <Suspense fallback={<LazyFallback />}>
        {!isShareablePage && view === 'staff-login' && (
          <StaffLogin
            onLogin={() => setView('staff')}
            onBack={() => setView('booking')}
          />
        )}
        {!isShareablePage && view === 'staff' && (
          <StaffDashboard
            config={config}
            onLogout={() => setView('booking')}
          />
        )}
        {!isShareablePage && view === 'admin-login' && (
          <AdminLogin
            onLogin={async () => {
              setView('admin');
              let token = '';
              try { token = JSON.parse(sessionStorage.getItem('admin_session') || '{}').token || ''; } catch {}
              const c = await loadConfig(token);
              if (c) setConfig(c);
            }}
            onBack={() => setView('booking')}
          />
        )}
        {!isShareablePage && view === 'admin' && (
          <AdminPanel
            config={config}
            bookings={bookings}
            onSaveConfig={handleSaveConfig}
            onBack={() => setView('booking')}
          />
        )}
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <FeedbackProvider>
          <OfflineBanner />
          <AppContent />
        </FeedbackProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
